import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import { api } from "../lib/api";

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

export default function Explore() {
  const [routes, setRoutes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [hoveredRouteId, setHoveredRouteId] = useState(null);
  const markerRefs = useRef({});

  useEffect(() => {
    let mounted = true;

    api
      .get("/summary")
      .then(({ data }) => {
        if (!mounted) return;
        setSummary(data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setSummary(null);
      });

    const fetchAll = async () => {
      try {
        const pageSize = 5;
        let page = 1;
        let all = [];
        while (mounted) {
          // request paginated routes
          const { data } = await api.get("/routes?include_gpx=true", {
            params: { page, page_size: pageSize },
          });
          const items = Array.isArray(data) ? data : data.items || [];
          const total = data.total || items.length || 0;
          all = all.concat(items);

          // Update routes progressively so the map can show partial data
          if (mounted) {
            setRoutes([...all]);
            if (page === 1) setFirstLoaded(true);
          }

          if (all.length >= total || items.length === 0) break;
          page += 1;
        }
        if (!mounted) return;
        setRoutes(all || []);
      } catch (e) {
        // fallback to single request
        try {
          const { data } = await api.get("/routes");
          if (!mounted) return;
          setRoutes(data || []);
          setFirstLoaded(true);
        } catch (_) {
          if (!mounted) return;
          setRoutes([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  // Sample GPX data to 1/10th for each route
  const routesWithSampledGPX = useMemo(() => {
    if (routes.length === 0) return [];

    return routes.map((route) => {
      let sampledGPX = [];
      if (
        route.gpx_points &&
        Array.isArray(route.gpx_points) &&
        route.gpx_points.length > 0
      ) {
        // Sample every 10th point
        sampledGPX = route.gpx_points.filter((_, i) => i % 10 === 0);
        // Always include the last point
        if (
          route.gpx_points.length > 0 &&
          !sampledGPX.includes(route.gpx_points[route.gpx_points.length - 1])
        ) {
          sampledGPX.push(route.gpx_points[route.gpx_points.length - 1]);
        }
      }
      return { ...route, sampledGPX };
    });
  }, [routes]);

  // Extract bounds from all routes' GPX points
  const bounds = useMemo(() => {
    if (routesWithSampledGPX.length === 0) return null;

    // Collect all sampled GPX points from all routes
    const allPoints = [];
    routesWithSampledGPX.forEach((route) => {
      if (route.sampledGPX && route.sampledGPX.length > 0) {
        route.sampledGPX.forEach((point) => {
          allPoints.push([point[0], point[1]]);
        });
      }
    });

    if (allPoints.length === 0) return null;
    return allPoints;
  }, [routesWithSampledGPX]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F6F0] to-white">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
          Exploration
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
          Discover Routes
        </h1>
        <p className="mt-6 text-[#5C665F] max-w-2xl">
          Explore every bikepacking route on the map, hover over any route to
          see details.
        </p>
      </div>

      {/* Map Container */}
      <div className="max-w-full h-[600px] relative">
        {!firstLoaded && loading ? (
          <div className="w-full h-full flex items-center justify-center bg-[#F9F6F0]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[#C86B4E] border-t-transparent mb-4"></div>
              <p className="text-[#5C665F]">Loading routes...</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={[46.5, 11.85]}
            zoom={3}
            className="w-full h-full rounded-2xl border border-[#E5E0D8]"
            style={{ margin: 0 }}
          >
            {/* If still loading additional pages, show a subtle overlay */}
            {loading && (
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
                <div className="bg-white/70 rounded-xl p-3 flex items-center gap-3">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-[#C86B4E] border-t-transparent"></div>
                  <div className="text-sm text-[#5C665F]">
                    Loading more routes...
                  </div>
                </div>
              </div>
            )}
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            {bounds && <FitBounds bounds={bounds} />}

            {routesWithSampledGPX.map((route) => {
              if (!route.sampledGPX || route.sampledGPX.length === 0)
                return null;

              // Convert GPX points to [lat, lng] format
              const routePoints = route.sampledGPX.map((p) => [p[0], p[1]]);
              const startPoint = routePoints[0];
              const endPoint = routePoints[routePoints.length - 1];
              const isHovered = hoveredRouteId === route.id;

              return (
                <div key={route.id}>
                  {/* Route polyline */}
                  <Polyline
                    positions={routePoints}
                    color="#C86B4E"
                    weight={isHovered ? 8 : 4}
                    opacity={isHovered ? 1 : 0.7}
                    dashArray={isHovered ? "" : "5, 5"}
                    lineCap="round"
                    lineJoin="round"
                    eventHandlers={{
                      mouseover: (e) => {
                        setHoveredRouteId(route.id);
                        e.target.setStyle({
                          weight: 8,
                          opacity: 1,
                          dashArray: "",
                        });
                      },
                      mouseout: (e) => {
                        setHoveredRouteId(null);
                        e.target.setStyle({
                          weight: 4,
                          opacity: 0.7,
                          dashArray: "5, 5",
                        });
                      },
                      click: () => {
                        if (markerRefs.current[route.id]) {
                          markerRefs.current[route.id].openPopup();
                        }
                      },
                    }}
                    style={{ cursor: "pointer" }}
                  />

                  {/* Start point marker with popup */}
                  <CircleMarker
                    ref={(el) => {
                      if (el) markerRefs.current[route.id] = el;
                    }}
                    center={startPoint}
                    radius={7}
                    fillOpacity={0.85}
                    color="#2C5545"
                    fillColor="#C86B4E"
                    weight={2}
                    eventHandlers={{
                      click: () => {
                        if (markerRefs.current[route.id]) {
                          markerRefs.current[route.id].openPopup();
                        }
                      },
                    }}
                  >
                    <Popup className="custom-popup" onClose={() => {}}>
                      <div className="w-64 p-0">
                        {/* Hero Image */}
                        {route.hero_image && (
                          <div className="relative w-full h-40 mb-3 rounded-lg overflow-hidden">
                            <img
                              src={route.hero_image}
                              alt={route.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        {/* Route Details */}
                        <div className="px-3 pb-3">
                          <h3 className="font-bold text-lg text-[#1A1D1A] mb-1">
                            {route.name}
                          </h3>

                          {route.location && (
                            <p className="text-sm text-[#5C665F] mb-2">
                              📍 {route.location}
                            </p>
                          )}

                          {route.description && (
                            <p className="text-sm text-[#5C665F] mb-3 line-clamp-3">
                              {route.description}
                            </p>
                          )}

                          {/* Quick Stats */}
                          {(route.distance_km || route.difficulty) && (
                            <div className="flex gap-3 text-xs mb-3">
                              {route.distance_km && (
                                <span className="bg-[#F9F6F0] px-2 py-1 rounded text-[#1A1D1A] font-semibold">
                                  {route.distance_km} km
                                </span>
                              )}
                              {route.difficulty && (
                                <span className="bg-[#F9F6F0] px-2 py-1 rounded text-[#1A1D1A] font-semibold">
                                  {route.difficulty}
                                </span>
                              )}
                            </div>
                          )}

                          {/* View Details Link */}
                          <Link
                            to={`/routes/${route.id}`}
                            className="inline-block w-full text-center bg-[#C86B4E] text-white py-2 rounded-lg font-semibold hover:bg-[#B85A3D] transition-colors text-sm"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>

                  {/* End point marker */}
                  <CircleMarker
                    center={endPoint}
                    radius={7}
                    fillOpacity={0.85}
                    color="#2C5545"
                    fillColor="#C86B4E"
                    weight={2}
                    eventHandlers={{
                      click: () => {
                        if (markerRefs.current[route.id]) {
                          markerRefs.current[route.id].openPopup();
                        }
                      },
                    }}
                  />
                </div>
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 max-w-2xl">
          <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
            <div className="text-3xl font-bold text-[#C86B4E]">
              {routes.length}
            </div>
            <div className="text-sm text-[#5C665F] mt-1">Total Routes</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
            <div className="text-3xl font-bold text-[#C86B4E]">
              {typeof summary?.creator_count === "number"
                ? summary.creator_count.toLocaleString()
                : "-"}
            </div>
            <div className="text-sm text-[#5C665F] mt-1">Number of Creators</div>
          </div>
        </div>
      </div>
    </div>
  );
}
